import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { koppelenStarten, ontkoppelen, sessieVerversen, useAanmeldProviders } from '../../api/auth.ts';
import { useIk, useSessie } from '../../auth/SessieContext.tsx';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { Kenmerken } from '../../componenten/Kenmerken.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { datumTijd } from '../../helpers/formatteer.ts';
import { gebruikersrolLabels } from '../../helpers/labels.ts';

/**
 * Het profiel van de aangemelde gebruiker: gegevens, sessie en gekoppelde aanmeldmethoden.
 * @returns De pagina.
 */
export function ProfielPagina() {
  const ik = useIk();
  const { herlaad, vervang, meldAf } = useSessie();
  const providers = useAanmeldProviders();
  const { meld, meldFout } = useMeldingen();
  const [ontkoppel, setOntkoppel] = useState<string | null>(null);
  const [overalAfmelden, setOveralAfmelden] = useState(false);
  const [bezig, setBezig] = useState(false);

  const gekoppeld = new Set(ik.identiteiten.map((identiteit) => identiteit.provider));
  const nogTeKoppelen = (providers.data ?? []).filter((provider) => !gekoppeld.has(provider.sleutel));
  const naamVan = (sleutel: string) =>
    providers.data?.find((provider) => provider.sleutel === sleutel)?.naam ?? sleutel;

  const koppel = (provider: string) => {
    setBezig(true);
    koppelenStarten(provider, new URL('/profiel', window.location.origin).href)
      .then((start) => window.location.assign(start.url))
      .catch((fout: unknown) => {
        meldFout(fout);
        setBezig(false);
      });
  };

  const ververs = () => {
    setBezig(true);
    sessieVerversen()
      .then((nieuw) => {
        vervang({ ...ik, sessie: nieuw.sessie, csrfToken: nieuw.csrfToken });
        meld('Sessie verlengd');
      })
      .catch(meldFout)
      .finally(() => setBezig(false));
  };

  return (
    <Pagina titel="Mijn profiel">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Gegevens" slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
            <CardContent>
              <Kenmerken
                items={[
                  { label: 'Naam', waarde: ik.gebruiker.naam },
                  {
                    label: 'E-mailadres',
                    waarde: `${ik.gebruiker.email}${ik.gebruiker.emailGeverifieerd ? ' (geverifieerd)' : ''}`,
                  },
                  { label: 'Rol', waarde: gebruikersrolLabels[ik.gebruiker.rol] },
                  { label: 'Status', waarde: <StatusChip soort="gebruiker" waarde={ik.gebruiker.status} /> },
                ]}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Sessie"
              slotProps={{ title: { variant: 'h6' } }}
              action={
                <Button size="small" startIcon={<RefreshIcon />} onClick={ververs} loading={bezig}>
                  Verlengen
                </Button>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Kenmerken
                items={[
                  { label: 'Aangemeld via', waarde: naamVan(ik.sessie.provider) },
                  { label: 'Aangemeld op', waarde: datumTijd(ik.sessie.aangemeldOp) },
                  { label: 'Verloopt bij inactiviteit', waarde: datumTijd(ik.sessie.verlooptOp) },
                  { label: 'Verloopt uiterlijk', waarde: datumTijd(ik.sessie.absoluutVerlooptOp) },
                ]}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="outlined" startIcon={<LogoutIcon />} onClick={() => void meldAf()}>
                  Afmelden
                </Button>
                <Button color="warning" onClick={() => setOveralAfmelden(true)}>
                  Overal afmelden
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <Card>
            <CardHeader title="Aanmeldmethoden" slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>E-mailadres</TableCell>
                    <TableCell>Gekoppeld op</TableCell>
                    <TableCell>Laatst gebruikt</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ik.identiteiten.map((identiteit) => (
                    <TableRow key={identiteit.provider}>
                      <TableCell sx={{ fontWeight: 500 }}>{naamVan(identiteit.provider)}</TableCell>
                      <TableCell>
                        {identiteit.email ?? '–'}
                        {identiteit.email && !identiteit.emailGeverifieerd ? ' (niet geverifieerd)' : ''}
                      </TableCell>
                      <TableCell>{datumTijd(identiteit.gekoppeldOp)}</TableCell>
                      <TableCell>{datumTijd(identiteit.laatstGebruiktOp)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<LinkOffIcon />}
                          disabled={ik.identiteiten.length < 2}
                          onClick={() => setOntkoppel(identiteit.provider)}
                        >
                          Ontkoppelen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {nogTeKoppelen.length > 0 && (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Extra aanmeldmethode koppelen:
                  </Typography>
                  {nogTeKoppelen.map((provider) => (
                    <Button
                      key={provider.sleutel}
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      disabled={bezig}
                      onClick={() => koppel(provider.sleutel)}
                    >
                      {provider.naam}
                    </Button>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <BevestigDialoog
        open={ontkoppel !== null}
        titel="Aanmeldmethode ontkoppelen"
        bevestigLabel="Ontkoppelen"
        gevaarlijk
        bezig={bezig}
        onSluit={() => setOntkoppel(null)}
        onBevestig={() => {
          if (ontkoppel) {
            setBezig(true);
            ontkoppelen(ontkoppel)
              .then(() => herlaad())
              .then(() => {
                meld('Aanmeldmethode ontkoppeld');
                setOntkoppel(null);
              })
              .catch(meldFout)
              .finally(() => setBezig(false));
          }
        }}
      >
        Je kunt je daarna niet meer aanmelden met {ontkoppel ? naamVan(ontkoppel) : ''}. Dit kan alleen kort na het
        aanmelden.
      </BevestigDialoog>
      <BevestigDialoog
        open={overalAfmelden}
        titel="Overal afmelden"
        bevestigLabel="Overal afmelden"
        gevaarlijk
        onSluit={() => setOveralAfmelden(false)}
        onBevestig={() => void meldAf(true)}
      >
        Je sessies op alle apparaten worden beëindigd.
      </BevestigDialoog>
    </Pagina>
  );
}
